import type { PrinterModelId } from './printerModelConfig';
import { MIN_XP_P203A_PRINT_WIDTH_MM } from './xpP203aLabelConfig';

const THERMAL_DPI = 203;

/** Printable dots at 203 dpi for a given width in mm. */
export function rasterDotsForWidthMm(widthMm: number): number {
  return Math.round((widthMm * THERMAL_DPI) / 25.4);
}

/** Character columns scale with printable width (XP-P203A baseline: 32 cols @ 48 mm). */
function charColumnsForWidthMm(widthMm: number, baselineMm = 48, baselineCols = 32): number {
  return Math.max(16, Math.round((widthMm / baselineMm) * baselineCols));
}

/** Printable width in dots (203 dpi class thermal). */
export function getReceiptRasterDotsPerLine(modelId: PrinterModelId): number {
  switch (modelId) {
    case 'xp-p203a':
      return rasterDotsForWidthMm(MIN_XP_P203A_PRINT_WIDTH_MM);
    case 'xp-p801a':
    case 'xp-p810':
    case 'xp-q838l':
    case 'xp-80t':
      return 576;
    default:
      return rasterDotsForWidthMm(MIN_XP_P203A_PRINT_WIDTH_MM);
  }
}

/** Character columns for text (normal font). */
export function getReceiptLineWidthForModel(modelId: PrinterModelId): number {
  switch (modelId) {
    case 'xp-p203a':
      return charColumnsForWidthMm(MIN_XP_P203A_PRINT_WIDTH_MM);
    case 'xp-p801a':
    case 'xp-p810':
    case 'xp-q838l':
    case 'xp-80t':
      return 48;
    default:
      return charColumnsForWidthMm(MIN_XP_P203A_PRINT_WIDTH_MM);
  }
}

export function separatorDashes(lineWidth: number): string {
  return '-'.repeat(Math.max(16, lineWidth));
}

/** Indented detail lines under a receipt item, wrapped (not truncated) to the printer width. */
export function itemDetailLines(details: readonly string[] | undefined, width: number, indent = '  '): string[] {
  const room = Math.max(8, width - indent.length);
  return (details ?? []).flatMap((d) => {
    const text = d.trim();
    const out: string[] = [];
    for (let i = 0; i < text.length; i += room) out.push(`${indent}${text.slice(i, i + room)}`);
    return out;
  });
}

/** Truncate or split a single centered header line to printer width. */
export function fitLineToWidth(text: string, width: number): string {
  const t = text.trim();
  if (t.length <= width) return t;
  return `${t.slice(0, Math.max(1, width - 1))}…`;
}
